import { HttpClient, HttpErrorResponse, HttpHeaders, HttpRequest } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';

import { combineLatest, from } from 'rxjs';
import { map, share, tap } from 'rxjs/operators';

import { BusyIndicatorService } from '../../../../gamma/busyindicator';
import { Mixin } from '../../../../gamma/core';
import { FormBaseComponent, IRegistrationFormConfigs } from '../../../../gamma/form/form-base.component';
import { FormMixin, IRegisteredForm } from '../../../../gamma/form/form.mixin';
import { IFormValidationMessage } from '../../../../gamma/form/formvalidationmessage.service';
import { HttpService, IInterceptor } from '../../../../gamma/http';
import { ReferentialService } from '../../../../gamma/referential';
import { UuidService } from '../../../../gamma/utils/uuid.service';

import { environment } from '../../../../environments/environment';
import { U2000RF_SecurityQuestions, U2000_TranslatePartialLoader } from '../../../U2000_core';
import { U2000_AlertServiceMixin } from '../../../U2000_core/U2000_mixins/U2000_alert-service.mixin';
import { IU2000_VerifyIdentityResult, U2000_VerifyIdentityNavigationStepsEnum } from './U2000_verifyidentity';

@Injectable()
@Mixin([U2000_AlertServiceMixin, FormMixin])
export class U2000_VerifyIdentityService implements U2000_AlertServiceMixin, FormMixin {
    // There to avoid using the constructor's name that is not available when minified.
    static className = 'U2000_VerifyIdentityService';

    //#region Mixin requirements
    // Alert mixin requirements
    alertsAdded: IU2000_Alert[];
    alertAdded$: EventEmitter<IU2000_Alert>;
    alertUpdated$: EventEmitter<IU2000_Alert>;
    alertsCleared$: EventEmitter<any>;
    initAlert: () => void;
    addAlert: (alert: IU2000_Alert) => void;
    updateAlert: (alert: IU2000_Alert) => void;

    clearAlerts: () => void;
    // Form mixin requirements
    formValidationMessages: IFormValidationMessage[];
    registeredForms: IRegisteredForm[];
    registerForm: (form: FormBaseComponent, configs: IRegistrationFormConfigs) => void;
    unregisterForm: (form: FormBaseComponent) => void;
    hasDirtyForms: () => boolean;
    cleanupForms: () => void;
    //#endregion

    _currentNavigationComponentId: U2000_VerifyIdentityNavigationStepsEnum;

    valid: EventEmitter<IU2000_VerifyIdentityResult>;
    error: EventEmitter<HttpErrorResponse>;
    componentConfigs: IU2000_VerifyIdentityInitResultDto;
    validatecodeResult: IU2000_VerifyIdentityCodeResultDto;
    sendCodeResult: IU2000_VerifyIdentitySendCodeResultDto;
    codeSendingMode: 'EMAIL' | 'SMS' = 'EMAIL';

    workflowInterceptor: IInterceptor;
    currentWorkflowReference: string;
    uniqueId: string;
    contactMediaControlFlowUuid: string;

    constructor(
        public http: HttpClient,
        private referentialService: ReferentialService,
        private httpService: HttpService,
        private translatePartialLoader: U2000_TranslatePartialLoader,
        private busyIndicatorService: BusyIndicatorService,
        private uuidService: UuidService,
    ) {
        this.initAlert();

        this.formValidationMessages = [];
        this.navigateTo(U2000_VerifyIdentityNavigationStepsEnum.verifyCode);

        this.workflowInterceptor = {
            request: (requestId: number, req: HttpRequest<any>) => {
                let newHeaders = req.headers ? req.headers : new HttpHeaders();
                if (!newHeaders.has('X-SAIA-Workflow-Reference') && this.currentWorkflowReference != null) {
                    newHeaders = newHeaders.set('X-SAIA-Workflow-Reference', this.currentWorkflowReference);
                }

                return req.clone({ headers: newHeaders });
            },
        };
    }

    resolve(workflowReference: string, uniqueId: string) {
        this.currentWorkflowReference = workflowReference;
        this.uniqueId = uniqueId;

        this.busyIndicatorService.show(null, 'Verify identity');
        const promise = new Promise((resolve, reject) => {
            combineLatest([this.initComponent(), this.initTranslation()]).subscribe(
                result => {
                    this.componentConfigs = result[0];
                    this.sendCodeResult = this.componentConfigs.sendCodeResult;

                    if (this.componentConfigs.securityEmail != null && this.componentConfigs.mobilePhoneNumber != null) {
                        this.navigateTo(U2000_VerifyIdentityNavigationStepsEnum.chooseCommunication);
                    }
                    this.busyIndicatorService.hide(null, 'Verify identity');
                    resolve(result[0]);
                },
                error => {
                    reject();

                    this.busyIndicatorService.hide(null, 'Verify identity');
                    this.error.emit(error);
                },
            );
        });

        return from(promise);
    }

    navigateTo(componentId: U2000_VerifyIdentityNavigationStepsEnum) {
        this._currentNavigationComponentId = componentId;
    }

    get currentNavigationComponentId() {
        return this._currentNavigationComponentId;
    }

    initComponent() {
        this.contactMediaControlFlowUuid = this.uuidService.generateUnsafeUuid();

        let url = environment.apiUrl + 'U2000/verifyIdentity/init';
        url = url + '?contactMediaControlFlowUuid=' + this.contactMediaControlFlowUuid;
        if (this.uniqueId != null) {
            url = url + '&uniqueId=' + this.uniqueId;
        }
        return this.http.get<IU2000_VerifyIdentityInitDtoResponse>(url, this.httpService.optionsWithExtra({ silent: true, globalErrorHandling: false })).pipe(
            map(res => {
                return res.result;
            }),
            tap(res => {
                this.componentConfigs = res;
                this.sendCodeResult = res.sendCodeResult;
                this.referentialService.add(U2000RF_SecurityQuestions, res.securityQuestions);
            }),
            share(),
        );
    }

    initTranslation() {
        return this.translatePartialLoader.getPartialTranslation('U2000_VerifyIdentity').pipe(share());
    }

    verifyIdentityCode(formValue: IU2000_VerifyIdentityCodeDtoRequest) {
        formValue.individualId = this.componentConfigs.individualId;
        formValue.contactMediaControlId = this.sendCodeResult.contactMediaControlId;
        formValue.contactMediaControlFlowUuid = this.contactMediaControlFlowUuid;
        formValue.securityKey = this.sendCodeResult.securityKey;

        return this.http.post<IU2000_VerifyIdentityCodeDtoResponse>(environment.apiUrl + 'U2000/verifyIdentity/validatecode', formValue).pipe(
            map(res => {
                return res.result;
            }),
            tap(res => {
                this.validatecodeResult = res;
            }),
        );
    }

    sendVerificationCode(formValue: IU2000_VerifyIdentitySendCodeDtoRequest) {
        this.codeSendingMode = formValue.verificationCodeTypeId === 0 ? 'EMAIL' : 'SMS';
        if (this.uniqueId) {
            formValue.uniqueId = this.uniqueId;
        }
        formValue.contactMediaControlFlowUuid = this.contactMediaControlFlowUuid;

        return this.http.post<IU2000_VerifyIdentitySendCodeDtoResponse>(environment.apiUrl + 'U2000/verifyIdentity/sendcode', formValue).pipe(
            map(res => {
                return res.result;
            }),
            tap(res => {
                this.sendCodeResult = res;
            }),
        );
    }

    resendVerificationCode(formValue: IU2000_VerifyIdentityResendCodeDtoRequest) {
        formValue.individualId = this.componentConfigs.individualId;
        formValue.contactMediaControlId = this.sendCodeResult.contactMediaControlId;
        formValue.contactMediaControlFlowUuid = this.contactMediaControlFlowUuid;
        formValue.securityKey = this.sendCodeResult.securityKey;
        return this.http.post<IU2000_VerifyIdentityResendCodeDtoResponse>(environment.apiUrl + 'U2000/verifyIdentity/resendcode', formValue).pipe(
            map(res => {
                return res.result;
            }),
            tap(res => {}),
        );
    }

    validateSecurityQuestion(formValue: IU2000_VerifyIdentitySecurityQuestionDtoRequest) {
        formValue.individualId = this.componentConfigs.individualId;
        return this.http.post<IU2000_VerifyIdentitySecurityQuestionDtoResponse>(environment.apiUrl + 'U2000/verifyIdentity/validatequestion', formValue);
    }

    onValid() {
        this.valid.emit({
            securityKey: this.sendCodeResult.securityKey,
            id: this.sendCodeResult.contactMediaControlId,
            expirationTimeStamp: this.sendCodeResult.expirationTimeStamp,
        });
    }
}
